import {AfterViewInit, Component, Inject, ViewChild} from '@angular/core';
import {ImportInitialValues, INITIAL_VALUES} from '../tokens';
import {FormComponent, LfGlobalValidationIssues} from 'lf-at-theme';
import {action, computed} from 'mobx-angular';
import {autorun, makeObservable} from 'mobx';
import {InfoCadastroResponseModel} from '../entities/InfoCadastroResponseModel';
import {RestService} from '../services/rest.service';
import {of} from 'rxjs';
import {ValidationIssues} from '@lightweightform/storage';

@Component({
  selector: 'app-import-form',
  templateUrl: './import-form.component.html',
  styleUrls: ['./import-form.component.scss']
})
export class ImportFormComponent implements AfterViewInit {

  @ViewChild('form') _lfForm: FormComponent;

  private _infoCadastroCache: Map<number, InfoCadastroResponseModel> = new Map();
  private _infoCadastroPendingReqs: Map<number, Promise<InfoCadastroResponseModel>> = new Map();

  constructor(
    @Inject(INITIAL_VALUES) public initialValues: ImportInitialValues,
    private restService: RestService,
    private _lfGlobalValidationIssues: LfGlobalValidationIssues
  ) {
    makeObservable(this);
  }

  ngAfterViewInit(): void {
    //Note: This autorun() call could be done with rxjs streams which would avoid converting the http request stream to a promise.
    //https://github.com/mobxjs/mobx-utils#tostream
    //https://blog.bitsrc.io/rxjs-patterns-efficiency-and-performance-10bbf272c3fc
    if (this.registeredByIntermediary && (this.initialValues.mode.new || this.initialValues.mode.edit)) {
      autorun(() => this.updateInfoCadastro());
    }
  }

  get hasCadastroData() {
    return (this.registeredByNetp && this.initialValues.hasCadastroData)
      || (this.registeredByIntermediary && this.shouldGetInfoCadastro)
  }

  get registeredByNetp() {
    return this.initialValues.registeredBy == 'NETP';
  }

  get registeredByIntermediary() {
    return this.initialValues.registeredBy == 'INTERMEDIARY';
  }

  get showCheckBoxChangePlaceOfBusiness(){
    return this.registeredByIntermediary && this.initialValues.mode.edit;
  }

  get countryOfBusinessIsEditable() {
    if (this.initialValues.mode.new) {
      return !this.registeredByNetp || this.initialValues.isNonResident;
    }
    if (this.initialValues.mode.edit) {
      return this.registeredByIntermediary && this.changePlaceOfBusiness;
    }
    return false;
  }

  get identOfBusinessIsEditable() {
    if (this.initialValues.mode.new) {
      return !this.registeredByNetp;
    }
    if (this.initialValues.mode.edit) {
      return this.registeredByIntermediary && this.changePlaceOfBusiness;
    }
    return false;
  }

  //@computed
  // Note: If it were @computed, it wouldn't be updated because it was called too early (before this._lfForm was bound)
  get shouldGetInfoCadastro() {
    const identificationOfBusiness = this._lfForm && this._lfForm.relativeStorage.get('./identificationOfBusiness');
    const vatIdFilled = identificationOfBusiness && identificationOfBusiness.issuingCountry == 'PT' && identificationOfBusiness.vatIdNum != '' && identificationOfBusiness.taxRefNum == '';
    const vatIdIsInteger = vatIdFilled && Number.isSafeInteger(Number.parseInt(identificationOfBusiness.vatIdNum));
    return vatIdFilled && vatIdIsInteger;
  }

  @computed
  get netpNif() {
    const identificationOfBusiness = this._lfForm && this._lfForm.relativeStorage.get('./identificationOfBusiness');
    return identificationOfBusiness && identificationOfBusiness.vatIdNum;
  }

  updateInfoCadastro() {
    if ((this.initialValues.mode.new || (this.initialValues.mode.edit && this.changePlaceOfBusiness)) && this.shouldGetInfoCadastro) {
      let infoCadastroModel = this.getInfoCadastro(this.netpNif);
      infoCadastroModel.then(value => {
        if (!value.hasCadastro) {
          this.setInfoCadastroValidation([{code: 'NIF_WITH_NO_CADASTRO'}]);
          this.clearInfoCadastroFromLfStorage();

        } else if (!value.hasVATActivity) {
          this.setInfoCadastroValidation([{code: 'NIF_WITH_NO_VAT_ACTIVITY'}]);
          this.clearInfoCadastroFromLfStorage();

        } else if (!value.hasFixedEstablishment) {
          this.setInfoCadastroValidation([{code: 'NIF_WITH_NO_FIXED_ESTAB'}]);
          this.clearInfoCadastroFromLfStorage();

        } else {
          this.applyInfoCadastroToLfStorage(value);
        }
      });
    }
  }

  getInfoCadastro(nif: number): Promise<InfoCadastroResponseModel> {
    if (this._infoCadastroCache.has(nif)) {
      return of(this._infoCadastroCache.get(nif)).toPromise();
    } else {
      if (this._infoCadastroPendingReqs.has(nif)) {
        return this._infoCadastroPendingReqs.get(nif);
      }
      const reqObserv = this.restService.getInfoCadastro(nif).toPromise();
      this._infoCadastroPendingReqs.set(nif, reqObserv);
      reqObserv
        .then((value) => {
          this._infoCadastroCache.set(nif, value);
        })
        .finally(() => {
          this._infoCadastroPendingReqs.delete(nif);
        });
      return reqObserv;
    }
  }

  get isGettingInfoCadastro() {
    return this._infoCadastroPendingReqs.size > 0;
  }

  @action
  applyInfoCadastroToLfStorage(infoCadastroModel: InfoCadastroResponseModel) {
    const storage = this._lfForm.relativeStorage;
    storage.set('./companyName', infoCadastroModel.companyName);
    storage.set('./countryOfBusiness', infoCadastroModel.countryOfBusiness);
    storage.set('./address', infoCadastroModel.address);
    if (storage.isUntouched('./contactInfo')) {
      storage.set('./contactInfo', infoCadastroModel.contactInfo);
    }
  }

  @action
  clearInfoCadastroFromLfStorage() {
    const storage = this._lfForm.relativeStorage;
    storage.reset('./companyName');
    storage.reset('./countryOfBusiness');
    storage.reset('./address');
    if (storage.isUntouched('./contactInfo')) {
      storage.reset('./contactInfo');
    }
  }

  @action
  setInfoCadastroValidation(validationIssues: ValidationIssues) {
    const path = this._lfForm.relativeStorage.relativeStorage('./identificationOfBusiness/vatIdNum').currentPath;
    this._lfGlobalValidationIssues.setFromPath(path, validationIssues)
  }

  showExtraAddressFields(address): boolean {
    return address.suite
      || address.postOfficeBox
      || address.district
      || address.countrySubEntity
      || address.otherLocalId;
  }

  get changePlaceOfBusiness() {
    return this._lfForm && this._lfForm.relativeStorage.get('./changePlaceOfBusiness');
  }
}
